"use client";

import React from "react";
import { CircleCheck, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const usps = ["Kwaliteitsborging door BKE-gecertificeerde assessoren", "Flexibele inzet voor alle hbo-onderwijssectoren", "Ontzorging van onderwijsmanagers en examencommissies"];
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-accent/20 border border-chart-2/20 rounded-lg text-center">
        <p className="text-primary font-medium mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline">
          Nog een bericht sturen
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-4">
        <FormField control={control} name="name" rules={{
        required: "Naam is verplicht"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-semibold">Naam</FormLabel>
              <FormControl>
                <Input placeholder="Uw volledige naam" {...field} className="bg-background border-border focus:border-chart-2" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-mail is verplicht",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ongeldig e-mailadres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-semibold">E-mail</FormLabel>
              <FormControl>
                <Input type="email" placeholder="uw@email.nl" {...field} className="bg-background border-border focus:border-chart-2" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Bericht is verplicht",
        minLength: {
          value: 10,
          message: "Minimaal 10 tekens"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-semibold">Bericht</FormLabel>
              <FormControl>
                <Textarea placeholder="Hoe kunnen wij u ondersteunen?" className="min-h-[120px] bg-background border-border focus:border-chart-2" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm font-medium">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-br from-primary to-chart-2 text-primary-foreground font-bold py-6">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Verzenden...
            </> : "Verstuur aanvraag"}
        </Button>
      </div>
    </Form>;
}
export default function InnovatieInVeiligheid() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="87913" id="ondersteuning-van-onderwijsinstellingen" className="py-16 lg:py-24 bg-background overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-7 relative">
            <div className="absolute -inset-4 md:-inset-6 lg:-inset-8 bg-accent/40 rounded-2xl -z-10 translate-x-4 translate-y-4 lg:translate-x-8 lg:translate-y-8" />
            <div className="w-full h-3 rounded-full bg-gradient-to-r from-primary via-chart-2 to-primary shadow-sm" />
          </div>

          <div className="lg:col-span-5 flex flex-col justify-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-6 leading-tight font-default">
              Strategische Partner voor HBO-Instellingen
            </h2>

            <p className="text-lg text-muted-foreground mb-8 leading-relaxed font-default">
              EduMedius is de verbindende schakel tussen onderwijsinstellingen en professionals. Wij fungeren als een one-stop-shop voor externe begeleiders, beoordelaars en docenten, met een sterke focus op kwaliteit en een persoonlijke, academische aanpak.
            </p>

            <ul className="space-y-4 mb-10">
              {usps.map((usp, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                  <CircleCheck className="size-6 text-chart-2 shrink-0 mt-0.5" aria-hidden="true" />
                  <span className="text-base font-medium text-foreground font-default">
                    {usp}
                  </span>
                </li>)}
            </ul>

            <div>
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="lg" className="text-base font-semibold px-8 py-6 bg-gradient-to-br from-primary to-chart-2 text-primary-foreground hover:opacity-90 transition-all duration-300 shadow-md hover:shadow-lg font-default cursor-pointer">
                    Maak kennis met EduMedius
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px] border-border bg-background">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold text-primary font-default">
                      Plan een vrijblijvend gesprek
                    </DialogTitle>
                  </DialogHeader>
                  <FormProvider formId="contact-innovatie-veiligheid" sectionName="innovatie-in-veiligheid" defaultValues={{
                  name: "",
                  email: "",
                  message: ""
                }} submitText="Verstuur aanvraag" successMessage="Bedankt voor uw aanvraag. We nemen zo snel mogelijk contact met u op.">
                    <ContactForm />
                  </FormProvider>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>
    </section>;
}